import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bool, func, number, string } from 'prop-types';
import { get, noop } from 'lodash';
import classNames from 'classnames';
import { Container } from '@fiverr-private/layout_components';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    ContactSellerButton,
    ContactSellerPopover,
    ContactSellerDrawer,
    fetchInitialData,
} from '@fiverr-private/send_message';
import { useGigPageContext } from '@fiverr-private/gig_page_context';
import { SlideFade } from '@fiverr-private/transition';
import { translate } from '@fiverr-private/i18n-react';
import { PAGE_NAME } from '../../../../utils/bigQueryEventsData';
import {
    redirectToInboxConversation,
    reportSendMessageSubmitSuccess,
    reportStickyChatClick,
} from '../../../../utils/contactSeller';
import { usePostFmpFetchMoreGigsData } from '../../../shared/contactSeller/hooks/usePostFmpFetchMoreGigsData';
import SafePureComponent from '../../../shared/SafePureComponent';
import ContactTrigger from '../../../shared/ContactTrigger';
import { updateShowFirstMessagePopup } from '../../../../actions/widgets';
import { selectIsSellerOnline } from '../../../../reducers/seller';
import { selectShowFirstMessagePopup } from '../../../../reducers/widgets';
import { INITIAL_REMINDER_INTERVAL_KEY, REMINDER_INTERVAL_MS } from './constants';
import { parseProficientLanguagesArrayToObject } from './utils';
import { gradientBorderStyle } from './FirstMessage.ve.css'
import './style.module.scss';

const FirstMessagePopover = ({
    profilePhoto,
    isOnline,
    responseTime,
    isMobileView,
    updateShowFirstMessagePopup = noop,
    showFirstMessagePopup = false,
}) => {
    const {
        logger,
        pathfinderWrapper,
        biEvents,
        general = {},
        sellerLanguageParams,
        seller,
        isBusinessLayout,
        floatingChatData
    } = useGigPageContext();
    const [contactSellerData, setContactSellerData] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const [reminderIntervalKey, setReminderIntervalKey] = useState(INITIAL_REMINDER_INTERVAL_KEY);
    const fetchPostFmpMoreGigsData = usePostFmpFetchMoreGigsData({ gigId: general.gigId });
    const { organizationId } = getContext();
    const triggeredRef = useRef(false);
    const proficientLanguagesObject = parseProficientLanguagesArrayToObject(
        sellerLanguageParams?.sellerValidatedLanguages
    );
    const ContactSellerEntry = organizationId ? ContactSellerDrawer : ContactSellerPopover;

    useEffect(() => {
        const reminderInterval = setInterval(() => {
            if (triggeredRef.current) {
                return clearInterval(reminderInterval);
            }

            setReminderIntervalKey((intervalKey) => ++intervalKey);
        }, REMINDER_INTERVAL_MS);

        return () => clearInterval(reminderInterval);
    }, []);

    const redirect = useCallback(() => redirectToInboxConversation(pathfinderWrapper), [pathfinderWrapper]);

    const triggerPopover = useCallback(async () => {
        try {
            const sellerUsername = getSellerUsername();

            const contactSellerData = await fetchInitialData(sellerUsername);

            setContactSellerData(contactSellerData);

            if (hadRecentInteractions(contactSellerData)) {
                redirect();

                return false;
            }

            setIsOpen(true);
            triggeredRef.current = true;
        } catch (error) {
            logger.warn(error, {
                description: 'Error while triggering contact seller popover',
            });

            redirect();
        }
    }, [logger, redirect]);

    useEffect(() => {
        if (showFirstMessagePopup) {
            triggerPopover();
        } else {
            setIsOpen(false);
        }
    }, [showFirstMessagePopup, triggerPopover]);

    const hadRecentInteractions = ({ priorInteraction }) => !!priorInteraction;

    const handleSubmitSuccess = useCallback(() => {
        reportSendMessageSubmitSuccess(biEvents);
    }, [biEvents]);

    const getRecipientInfo = () => {
        const { sellerId } = general;
        const username = getSellerUsername();
        const recipientInfo = get(contactSellerData, 'recipient', {});

        return {
            username,
            profilePhoto,
            isOnline,
            responseTimeInHrs: responseTime,
            sellerId,
            proficientLanguages: proficientLanguagesObject,
            displayName: seller.displayName,
            ...recipientInfo,
        };
    };

    const getPopoverProps = () => {
        const { gigId, subCategoryId } = general;
        const recipientInfo = getRecipientInfo();
        const senderInfo = get(contactSellerData, 'sender', {});
        const sourceComponent = `${PAGE_NAME}_sticky_contact`;

        return {
            onSubmitSuccess: handleSubmitSuccess,
            relatedGigId: gigId,
            withTrigger: true,
            subCategoryId,
            recipientInfo,
            senderInfo,
            attachmentsUploadConfig: contactSellerData?.attachmentsUploadConfig,
            source: sourceComponent,
            showInfo: true,
            pageName: PAGE_NAME,
            offerMoreGigsEnabled: true,
            getMoreGigsData: fetchPostFmpMoreGigsData,
            encourageAppDownloadEnabled: true,
            inFmpMoreGigsV4Rollout: false,
            withSatisfactionGuarantee: seller.isPro && isBusinessLayout && !!organizationId
        };
    };

    const getSellerUsername = () => {
        const { pathParameters } = getContext();
        const { username: sellerUsername } = pathParameters;

        return sellerUsername;
    };

    const onClick = () => {
        const { isOnline, responseTimeInHrs } = getRecipientInfo();

        reportStickyChatClick({
            biEvents,
            params: { isOnline, responseTime: responseTimeInHrs },
        });
    };

    const onClose = () => {
        updateShowFirstMessagePopup(false);
    };

    return (
        <ContactTrigger
            onClick={onClick}
            source="first-message"
            triggerId="Contact Seller"
            triggerCopy={translate('send_message.message')}
            triggerPlacement="Sticky left"
        >
            {({ onClick }) => (
                <SlideFade key={reminderIntervalKey} in duration="moderate3" offsetY="lg" easing="easeInOut">
                    <Container as="section" marginLeft="8" zIndex="overlay" position="sticky" display="inlineBlock" bottom="32px">
                        <ContactSellerEntry
                            {...getPopoverProps()}
                            isOpen={isOpen}
                            onTrigger={onClick}
                            isMobile={isMobileView}
                            onClose={onClose}
                        >
                            <div data-testid="contact-seller-trigger">
                                <Container className={classNames({ [gradientBorderStyle]: floatingChatData?.isEligible })} position="relative">
                                    {/* This ContactSellerButton is from send_message 5.12.4 which adds gradient bubble with flag isAssistantActive */}
                                    <ContactSellerButton isMobile={isMobileView} recipientInfo={getRecipientInfo()} isAssistantActive={floatingChatData?.isEligible}/>
                                </Container>
                            </div>
                        </ContactSellerEntry>
                    </Container>
                </SlideFade>
            )}
        </ContactTrigger>
    );
};

FirstMessagePopover.propTypes = {
    profilePhoto: string,
    isOnline: bool,
    responseTime: number,
    isMobileView: bool,
    showFirstMessagePopup: bool,
    updateShowFirstMessagePopup: func,
};

const mapStateToProps = (state) => ({
    isOnline: selectIsSellerOnline(state),
    showFirstMessagePopup: selectShowFirstMessagePopup(state),
});

const mapDispatchToProps = { updateShowFirstMessagePopup };

const ConnectedFirstMessagePopover = connect(mapStateToProps, mapDispatchToProps)(FirstMessagePopover);

export { FirstMessagePopover };
export default SafePureComponent(ConnectedFirstMessagePopover);
